import {
  Constants,
  MasterPropertyCategories,
  MasterPropertyType,
  ReductionMeasureLists,
} from "constants/constant";
import masterPropertyApi from "middlewares/MasterProperty";
import { ReductionMeasureModel } from "object-models/ReductionMeasures";
import { MasterPropertyModel } from "object-models/shared/master-property.model";
import React, { useEffect, useRef, useState } from "react";
import CreateUpdateReductionMeasuresDesign from "./CreateUpdateReductionMeasures.design";
import numericValidator from "services/numericValidator";
import { SelectChangeEvent } from "@mui/material/Select/SelectInput";
import {
  _addReductionMeasure,
  _getReductionMeasure,
  _updateReductionMeasure,
  _getGHGEmissionFactors,
  _getGHGEmissionFactorUnits,
} from "middlewares/ReductionMeasure/ReductionMeasureApi";
import Spinner from "components/Spinner/Spinner";
import { AlertsProps } from "components/Alerts/data/Alerts.type";
import { useParams } from "react-router-dom";
import { GHGCarrierModel } from "object-models/GHGCarrierModel";
import { GHGEmissionFactorModel } from "object-models/GHGEmissionFactorModel";
import { _getLocation } from "middlewares/LocationApi/location";
import { EFMappingElectricityModel } from "object-models/shared/EFMappingElectricityModel";
import { useAppState } from "context/appState.context";
import { Alert } from "@mui/material";
import {
  getFriendlyErrorMessage,
  reportUiError,
} from "services/ui-error-reporter";

interface CreateUpdateReductionMeasuresProps {
  closeDialog: () => void;
  isEdit?: boolean;
  selectedReductionMeasureId: number;
  handleCloseSnackBar: (state: AlertsProps) => void;
  isRefreshEditClose: () => void;
}
export default function CreateUpdateReductionMeasures(
  props: Readonly<CreateUpdateReductionMeasuresProps>
) {
  const { LocationId } = useParams();
  const { clientId } = useAppState();
  const defaultReductionMeasure: ReductionMeasureModel = {
    id: 0,
    locationId: +(LocationId ?? "0"),
    reductionMeasureId: 0,
    reductionMeasureName: "",
    businessUnit: "",
    dataSources: "",
    measureStage: "",
    measureCategory: "",
    measureType: "",
    assumptions: "",
    measureDescription: "",
    ghgReduction: "",
    noOfCarrier: 0,
    ghgCarriers: [],
    startYear: undefined,
    endYear: undefined,
    pace: 100,
    execution: 100,
    techLife: 10,
    financialLifetime: 10,
    measureTimingNotes: "",
    easeRating: undefined,
    reputationRating: undefined,
    climateRiskRating: undefined,
    biodiversityRating: undefined,
    communityRating: undefined,
    technologyRedinessRating: undefined,
    operationalDisruptionRating: undefined,
    healthSafetyRating: undefined,
    isActive: false,
    startQuarter: undefined,
    endQuarter: undefined,
    capExInvestment: undefined,
    opEx: undefined,
    revenue: undefined,
    personnelTime: undefined,
    personnelRate: undefined,
    externalFunding: undefined,
    financialNotes: "",
  };

  const isGhgEmissionsSelection = (value?: string): boolean => {
    return (
      value === ReductionMeasureLists.EmissionTypeList[1] ||
      value === "GHG Emission"
    );
  };
  const createDefaultGhgCarrier = (): GHGCarrierModel => ({
    id: 0,
    reductionMeasureId: 0,
    ghgCarrierId: 0,
    annualReduction: 0,
    notes: "",
    isActive: true,
    ghgScope: "",
    ghgCategory: "",
    country: Constants.DefaultCountry,
    region: Constants.DefaultRegion,
  });

  type ReductionMode = "energy" | "ghgEmission";

  const getReductionMode = (value?: string): ReductionMode | undefined => {
    if (!value) {
      return undefined;
    }

    return isGhgEmissionsSelection(value) ? "ghgEmission" : "energy";
  };

  const cloneCarrier = (carrier: GHGCarrierModel): GHGCarrierModel => ({
    ...carrier,
    customGHGEmissionFactor: carrier.customGHGEmissionFactor
      ? { ...carrier.customGHGEmissionFactor }
      : undefined,
    CustomGHGEmissionFactor: carrier.CustomGHGEmissionFactor
      ? { ...carrier.CustomGHGEmissionFactor }
      : undefined,
  });

  const cloneCarriers = (carriers: GHGCarrierModel[]): GHGCarrierModel[] => {
    return carriers.map((carrier) => cloneCarrier(carrier));
  };

  const modeCarrierDraftsRef = useRef<
    Partial<Record<ReductionMode, { noOfCarrier: number; ghgCarriers: GHGCarrierModel[] }>>
  >({});

  const [businessUnitList, setBusinessUnitList] = useState(
    [] as Array<string | undefined>
  );

  const [loading, setLoading] = useState(false);
  const [energyCategoryList, setEnergyCategoryList] = useState(
    [] as Array<MasterPropertyModel>
  );
  const [energyTypeList, setEnergyTypeList] = useState(
    [] as Array<MasterPropertyModel>
  );
  const [measureStageList, setMeasureStageList] = useState(
    [] as Array<MasterPropertyModel>
  );
  const [measureCategoryList, setMeasureCategoryList] = useState(
    [] as Array<MasterPropertyModel>
  );
  const [measureTypeList, setMeasureTypeList] = useState(
    [] as Array<MasterPropertyModel>
  );

  const [ghgCategoryList, setGHGCategoryList] = useState(
    [] as Array<MasterPropertyModel>
  );

  const [gHGScopeList, setGHGScopeList] = useState(
    [] as Array<MasterPropertyModel>
  );

  const [quarterList, setQuarterList] = useState(
    [] as Array<MasterPropertyModel>
  );
  const [draftReductionMeasure, setDraftReductionMeasure] = useState(
    defaultReductionMeasure
  );

  const [implementationComplexityList, setImplementationComplexityList] =
    useState([] as Array<MasterPropertyModel>);
  const [reputationList, setReputationList] = useState(
    [] as Array<MasterPropertyModel>
  );
  const [climateRiskList, setClimateRiskList] = useState(
    [] as Array<MasterPropertyModel>
  );
  const [biodiversityList, setBiodiversityList] = useState(
    [] as Array<MasterPropertyModel>
  );
  const [communityList, setCommunityList] = useState(
    [] as Array<MasterPropertyModel>
  );
  const [techReadinessList, setTechReadinessList] = useState(
    [] as Array<MasterPropertyModel>
  );
  const [opDisruptionList, setOpDisruptionList] = useState(
    [] as Array<MasterPropertyModel>
  );
  const [healthSafetyList, setHealthSafetyList] = useState(
    [] as Array<MasterPropertyModel>
  );

  const [countryList, setCountryList] = useState<EFMappingElectricityModel[]>(
    []
  );

  const [regionList, setRegionList] = useState<string[]>([]);

  const [ghgEmissionFactorList, setGhgEmissionFactorList] = useState<GHGEmissionFactorModel[]>([]);
  const [ghgUnitOptionsByCarrier, setGhgUnitOptionsByCarrier] = useState<Record<number, string[]>>({});
  const [formErrorMessage, setFormErrorMessage] = useState<string | null>(null);

  // Track in-flight API requests per carrier to prevent race conditions
  const ghgUnitRequestsRef = useRef<Record<number, AbortController>>({});

  const isAbortLikeError = (err: unknown): boolean => {
    if (err instanceof DOMException && err.name === "AbortError") {
      return true;
    }

    if (typeof err === "object" && err !== null && "name" in err) {
      return (err as { name?: string }).name === "AbortError";
    }

    return false;
  };

  const resolveEffectiveCarrierType = (carrier: GHGCarrierModel): string => {
    const carrierType = carrier.ghgType?.trim();
    const resolvedCustomType = [
      carrier.customGHGType?.trim(),
      carrier.CustomGHGType?.trim(),
      carrier.customGHGEmissionFactor?.ghgType?.trim(),
      carrier.CustomGHGEmissionFactor?.GHGType?.trim(),
    ].find((value): value is string => value != null && value.length > 0);

    return carrierType && carrierType !== "Custom" ? carrierType : (resolvedCustomType ?? "");
  };

  const ghgTypeSignature = draftReductionMeasure.ghgCarriers
    .map((carrier) => resolveEffectiveCarrierType(carrier))
    .join("|");

  const sanitizeUnitList = (values: Array<string | undefined | null>): string[] => {
    return Array.from(
      new Set(
        values.filter(
          (unit): unit is string =>
            Boolean(unit && unit.trim().length > 0) && unit !== "Custom"
        )
      )
    );
  };

  const fallbackUnitsForCarrier = (ghgType?: string, currentUnit?: string): string[] => {
    const predefinedGHGTypes = new Set(ghgEmissionFactorList.map((g) => g.ghgType));
    const isPredefinedType = Boolean(ghgType && predefinedGHGTypes.has(ghgType));

    const units = isPredefinedType
      ? ghgEmissionFactorList
          .filter((g) => g.ghgType === ghgType)
          .map((g) => g.ghgUnit)
      : ghgEmissionFactorList.map((g) => g.ghgUnit);

    return sanitizeUnitList([...units, currentUnit]);
  };

  const loadGhgUnitOptionsForCarrier = async (
    carrier: GHGCarrierModel,
    index: number
  ) => {
    const effectiveCarrierType = resolveEffectiveCarrierType(carrier);
    const rawCarrierType = carrier.ghgType?.trim();
    
    // Cancel any in-flight request for this carrier to prevent stale overwrites
    if (ghgUnitRequestsRef.current[index]) {
      ghgUnitRequestsRef.current[index].abort();
    }

    if (!effectiveCarrierType || effectiveCarrierType === "Custom") {
      setGhgUnitOptionsByCarrier((prev) => ({ ...prev, [index]: [] }));
      delete ghgUnitRequestsRef.current[index];
      return;
    }

    // When a carrier has a raw non-Custom ghgType but emission factors are not loaded yet,
    // defer the unit fetch to avoid misclassifying predefined types as custom.
    if (
      rawCarrierType &&
      rawCarrierType !== "Custom" &&
      ghgEmissionFactorList.length === 0
    ) {
      setGhgUnitOptionsByCarrier((prev) => ({ ...prev, [index]: [] }));
      delete ghgUnitRequestsRef.current[index];
      return;
    }

    // Clear stale options immediately so UI does not show previous type's units
    // while the latest request is in-flight.
    setGhgUnitOptionsByCarrier((prev) => ({ ...prev, [index]: [] }));

    const abortController = new AbortController();
    ghgUnitRequestsRef.current[index] = abortController;

    const predefinedGHGTypes = new Set(ghgEmissionFactorList.map((g) => g.ghgType));
    const isPredefinedType = predefinedGHGTypes.has(effectiveCarrierType);

    try {
      const apiUnits = await _getGHGEmissionFactorUnits({
        ghgType: isPredefinedType ? effectiveCarrierType : undefined,
        customGHGType: isPredefinedType ? undefined : effectiveCarrierType,
        signal: abortController.signal,
      });

      // Only update state if this request was not aborted (i.e., it's the latest request for this carrier)
      if (!abortController.signal.aborted) {
        setGhgUnitOptionsByCarrier((prev) => ({
          ...prev,
          [index]: sanitizeUnitList([...(apiUnits ?? []), carrier.ghgUnit]),
        }));
        delete ghgUnitRequestsRef.current[index];
      }
    } catch (err) {
      if (abortController.signal.aborted || isAbortLikeError(err)) {
        delete ghgUnitRequestsRef.current[index];
        return;
      }

      console.error("Failed to load GHG unit options", {
        index,
        effectiveCarrierType,
        err,
      });

      setGhgUnitOptionsByCarrier((prev) => ({
        ...prev,
        [index]: fallbackUnitsForCarrier(effectiveCarrierType, carrier.ghgUnit),
      }));
      delete ghgUnitRequestsRef.current[index];
    }
  };

  function fieldChangeHandler(
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent
  ) {
    const numericCheckColumns = [
      "pace",
      "execution",
      "techLife",
      "financialLifetime",
      "capExInvestment",
      "opEx",
      "revenue",
      "personnelTime",
      "personnelRate",
      "externalFunding",
    ];

    if (event.target.name === "ghgReduction") {
      const nextReduction = event.target.value.toString();

      setDraftReductionMeasure((prev) => {
        const previousMode = getReductionMode(prev.ghgReduction);
        const nextMode = getReductionMode(nextReduction);

        if (previousMode && prev.ghgCarriers.length > 0) {
          modeCarrierDraftsRef.current[previousMode] = {
            noOfCarrier: prev.noOfCarrier ?? prev.ghgCarriers.length,
            ghgCarriers: cloneCarriers(prev.ghgCarriers),
          };
        }

        let nextNoOfCarrier = prev.noOfCarrier;
        let nextGhgCarriers = prev.ghgCarriers;
        const hasReductionModeChanged = prev.ghgReduction !== nextReduction;

        if (nextMode) {
          const cachedDraft = modeCarrierDraftsRef.current[nextMode];

          if (cachedDraft && cachedDraft.ghgCarriers.length > 0) {
            nextNoOfCarrier = cachedDraft.noOfCarrier;
            nextGhgCarriers = cloneCarriers(cachedDraft.ghgCarriers);
          } else if (hasReductionModeChanged && prev.ghgReduction.length > 0) {
            nextNoOfCarrier = 1;
            nextGhgCarriers = [createDefaultGhgCarrier()];
          }
        }

        return {
          ...prev,
          ghgReduction: nextReduction,
          noOfCarrier: nextNoOfCarrier,
          ghgCarriers: nextGhgCarriers,
        };
      });

      return;
    }

    let newValue;
    if (numericCheckColumns.includes(event.target.name)) {
      if (
        event.target.name === "opEx" ||
        event.target.name === "personnelTime" ||
        event.target.name === "revenue"
      ) {
        newValue = numericValidator.allowNumericValue(event.target.value, true);
      } else {
        newValue = numericValidator.allowNumericValue(event.target.value);
      }
    } else {
      newValue = event.target.value;
    }
    setDraftReductionMeasure({
      ...draftReductionMeasure,
      [event.target.name]: newValue,
    });
  }

  const updateProperty = (index: number, name: string, value: string) => {
    if (
      name === "ghgScope" &&
      ghgCategoryList.filter((c) => c.type === value.replace(" ", "")).length <
        1
    ) {
      setLoading(true);
      getGHGCategoryListByScope(value);
    }
    setDraftReductionMeasure((prev) => {
      const updatedGhgCarriers = [...prev.ghgCarriers];
      let updatedValue: string | number = value;

      if (name === "annualReduction" || name === "customCO2e_EF") {
        updatedValue = numericValidator.allowNumericValue(value, true);
      }

      updatedGhgCarriers[index] = {
        ...updatedGhgCarriers[index],
        [name]: updatedValue,
      };

      if (name === "ghgType") {
        updatedGhgCarriers[index].customGHGType = "";
        updatedGhgCarriers[index].ghgUnit = "";
        updatedGhgCarriers[index].customGHGUnit = "";
        updatedGhgCarriers[index].CustomGHGUnit = "";
        const customGhgEmissionFactor =
          updatedGhgCarriers[index].CustomGHGEmissionFactor;
        if (customGhgEmissionFactor) {
          customGhgEmissionFactor.GHGUnit = "";
        }
        const customGhgEmissionFactorLower =
          updatedGhgCarriers[index].customGHGEmissionFactor;
        if (customGhgEmissionFactorLower) {
          customGhgEmissionFactorLower.ghgUnit = "";
        }
        const predefinedGHGTypes = new Set(
          ghgEmissionFactorList.map((g) => g.ghgType)
        );
        if (predefinedGHGTypes.has(value)) {
          updatedGhgCarriers[index].customCO2e_EF = "";
        }
      }

      if (name === "ghgUnit") {
        updatedGhgCarriers[index].customGHGUnit = "";
        updatedGhgCarriers[index].CustomGHGUnit = "";
        const customGhgEmissionFactor =
          updatedGhgCarriers[index].CustomGHGEmissionFactor;
        if (customGhgEmissionFactor) {
          customGhgEmissionFactor.GHGUnit = "";
        }
        const customGhgEmissionFactorLower =
          updatedGhgCarriers[index].customGHGEmissionFactor;
        if (customGhgEmissionFactorLower) {
          customGhgEmissionFactorLower.ghgUnit = "";
        }
      }

      return { ...prev, ghgCarriers: updatedGhgCarriers };
    });
  };

  function ghgCarrierfieldChangeHandler(
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent,
    index: number
  ) {
    if (event.target.name === "country") {
      const regions = countryList
        .filter((c) => c.country === event.target.value)
        .map((c) => c.region)
        .filter((region): region is string => region !== undefined);

      setRegionList(regions);
    }
    updateProperty(index, event.target.name, event.target.value);
  }

  useEffect(() => {
    if (
      draftReductionMeasure.noOfCarrier! <
      draftReductionMeasure.ghgCarriers?.length
    ) {
      draftReductionMeasure.ghgCarriers?.splice(
        draftReductionMeasure.noOfCarrier ?? 0,
        draftReductionMeasure.ghgCarriers.length
      );
      setDraftReductionMeasure({ ...draftReductionMeasure });
      return;
    }
    while (
      draftReductionMeasure.noOfCarrier! >
      draftReductionMeasure.ghgCarriers?.length
    ) {
      draftReductionMeasure.ghgCarriers?.push(createDefaultGhgCarrier());
    }
    setDraftReductionMeasure({ ...draftReductionMeasure });
  }, [draftReductionMeasure.noOfCarrier]);

  useEffect(() => {
    if (!isGhgEmissionsSelection(draftReductionMeasure.ghgReduction)) {
      Object.values(ghgUnitRequestsRef.current).forEach((controller) => {
        if (controller && !controller.signal.aborted) {
          controller.abort();
        }
      });
      ghgUnitRequestsRef.current = {};
      setGhgUnitOptionsByCarrier({});
      return;
    }

    const validIndices = new Set(
      draftReductionMeasure.ghgCarriers.map((_, index) => index)
    );

    // Abort and remove in-flight requests for carriers that no longer exist.
    Object.keys(ghgUnitRequestsRef.current).forEach((key) => {
      const index = Number(key);
      if (!validIndices.has(index)) {
        const controller = ghgUnitRequestsRef.current[index];
        if (controller && !controller.signal.aborted) {
          controller.abort();
        }
        delete ghgUnitRequestsRef.current[index];
      }
    });

    // Drop stale unit option entries for removed carrier indices.
    setGhgUnitOptionsByCarrier((prev) => {
      const next: Record<number, string[]> = {};
      Object.entries(prev).forEach(([key, value]) => {
        if (validIndices.has(Number(key))) {
          next[Number(key)] = value;
        }
      });

      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });

    draftReductionMeasure.ghgCarriers.forEach((carrier, index) => {
      if (resolveEffectiveCarrierType(carrier).length > 0) {
        loadGhgUnitOptionsForCarrier(carrier, index);
      }
    });
  }, [
    draftReductionMeasure.ghgReduction,
    ghgTypeSignature,
    ghgEmissionFactorList,
    props.isEdit,
    props.selectedReductionMeasureId,
  ]);

  async function getReductionMeasureById() {
    _getReductionMeasure(props.selectedReductionMeasureId)
      .then((res) => {
        if (res.ghgCarriers) {
          res.noOfCarrier = res.ghgCarriers.length;

          // Map Energy fields back to GHG fields when GHG Emission is selected
          if (isGhgEmissionsSelection(res.ghgReduction)) {
            res.ghgCarriers.forEach((carrier) => {
              if (!carrier.ghgType && carrier.energyType) {
                carrier.ghgType = carrier.energyType;
              }
              if (!carrier.ghgUnit && carrier.energyUnits) {
                carrier.ghgUnit = carrier.energyUnits;
              }
            });
          }

          setDraftReductionMeasure(res);
          getGHGCategoryList();
        }

        setLoading(false);
      })
      .catch((err) => {
        const message = getFriendlyErrorMessage(
          err,
          "We could not load this reduction measure right now. Please try again."
        );
        setFormErrorMessage(message);
        reportUiError(err, {
          feature: "ReductionMeasure",
          action: "load measure for edit",
          component: "CreateUpdateReductionMeasures",
          route: typeof window !== "undefined" ? window.location.pathname : undefined,
          extra: {
            reductionMeasureId: props.selectedReductionMeasureId,
            locationId: LocationId,
          },
        });
        setLoading(false);
      });
  }
  function create() {
    const formattedData = preprocessReductionMeasure(draftReductionMeasure, ghgEmissionFactorList);
    _addReductionMeasure(formattedData)
      .then((res) => {
        if (res) {
          const message = `${draftReductionMeasure.reductionMeasureName} Created Successfully !`;
          setDraftReductionMeasure(res);
          props.handleCloseSnackBar({
            show: true,
            severity: "success",
            message: message,
          });
          setLoading(false);
          props.closeDialog();
        }
      })
      .catch((err) => {
        const message = getFriendlyErrorMessage(
          err,
          "We could not add the reduction measure right now. Please try again."
        );
        setFormErrorMessage(message);
        reportUiError(err, {
          feature: "ReductionMeasure",
          action: "add measure",
          component: "CreateUpdateReductionMeasures",
          route: typeof window !== "undefined" ? window.location.pathname : undefined,
          extra: {
            locationId: LocationId,
            reductionMeasureName: draftReductionMeasure.reductionMeasureName,
          },
        });
        setLoading(false);
      });
  }

  function update() {
    const formattedData = preprocessReductionMeasure(draftReductionMeasure, ghgEmissionFactorList);
    _updateReductionMeasure(formattedData)
      .then((res) => {
        if (res) {
          const message = "Data Saved Successfully !";
          setDraftReductionMeasure(res);
          props.handleCloseSnackBar({
            show: true,
            severity: "success",
            message: message,
          });
          setLoading(false);
          props.closeDialog();
          props.isRefreshEditClose();
        }
      })
      .catch((err) => {
        const message = getFriendlyErrorMessage(
          err,
          "We could not save this reduction measure right now. Please try again."
        );
        setFormErrorMessage(message);
        reportUiError(err, {
          feature: "ReductionMeasure",
          action: "update measure",
          component: "CreateUpdateReductionMeasures",
          route: typeof window !== "undefined" ? window.location.pathname : undefined,
          extra: {
            reductionMeasureId: draftReductionMeasure.reductionMeasureId,
            locationId: LocationId,
          },
        });
        setLoading(false);
      });
  }
  function onSave() {
    setFormErrorMessage(null);
    if (props.isEdit !== undefined && props.isEdit === true) {
      setLoading(true);
      update();
    } else {
      setLoading(true);
      create();
    }
  }

  function preprocessReductionMeasure(
    draftReductionMeasure: ReductionMeasureModel,
    ghgEmissionFactorList: GHGEmissionFactorModel[]
  ) {
    const payload: ReductionMeasureModel = {
      ...draftReductionMeasure,
      ghgCarriers: cloneCarriers(draftReductionMeasure.ghgCarriers),
    };

    // Extract predefined GHG types and build a map of valid units per type
    const predefinedGHGTypes = new Set(ghgEmissionFactorList.map((g) => g.ghgType));
    const unitsByType = new Map<string, Set<string>>();
    ghgEmissionFactorList.forEach((g) => {
      if (!unitsByType.has(g.ghgType)) {
        unitsByType.set(g.ghgType, new Set());
      }
      unitsByType.get(g.ghgType)!.add(g.ghgUnit);
    });

    const isGHGEmissionMode = isGhgEmissionsSelection(
      payload.ghgReduction
    );

    const isCustomGHGType = (carrier: GHGCarrierModel) =>
      !!carrier.ghgType && !predefinedGHGTypes.has(carrier.ghgType);

    const isCustomGHGUnit = (carrier: GHGCarrierModel) =>
      !!carrier.ghgUnit &&
      !!carrier.ghgType &&
      (!unitsByType.has(carrier.ghgType) ||
        !unitsByType.get(carrier.ghgType)!.has(carrier.ghgUnit));

    const syncCustomEmissionFactors = (carrier: GHGCarrierModel) => {
      const type = carrier.customGHGType ?? "";
      const unit = carrier.customGHGUnit ?? "";
      const ef = carrier.customCO2e_EF ?? "";

      carrier.CustomGHGType = type;
      carrier.CustomGHGUnit = unit;
      carrier.CustomCO2e_EF = ef;

      if (carrier.CustomGHGEmissionFactor) {
        carrier.CustomGHGEmissionFactor.GHGType = type;
        carrier.CustomGHGEmissionFactor.GHGUnit = unit;
        carrier.CustomGHGEmissionFactor.CO2e_EF = ef;
      }
      if (carrier.customGHGEmissionFactor) {
        carrier.customGHGEmissionFactor.ghgType = type;
        carrier.customGHGEmissionFactor.ghgUnit = unit;
        carrier.customGHGEmissionFactor.cO2e_EF = ef;
      }
    };

    const normalizeLocation = (carrier: GHGCarrierModel) => {
      if (
        carrier.ghgScope !== "Scope2" &&
        carrier.ghgCategory !== "Electricity" &&
        carrier.ghgCategory !== "Steam"
      ) {
        carrier.country = Constants.DefaultCountry;
        carrier.region = Constants.DefaultRegion;
      }
    };

    const mapGhgToEnergy = (carrier: GHGCarrierModel) => {
      if (!isGHGEmissionMode) return;
      if (carrier.ghgType) carrier.energyType = carrier.ghgType;
      if (carrier.ghgUnit) carrier.energyUnits = carrier.ghgUnit;
    };

    const applyCustomFields = (
      carrier: GHGCarrierModel,
      typeIsCustom: boolean,
      unitIsCustom: boolean
    ) => {
      // Preserve backward-compat literal "Custom" value; otherwise lift ghgType/ghgUnit into custom slots.
      if (carrier.ghgType !== "Custom" && typeIsCustom) {
        carrier.customGHGType = carrier.ghgType;
      }
      if (carrier.ghgUnit !== "Custom" && unitIsCustom) {
        carrier.customGHGUnit = carrier.ghgUnit;
      }
      syncCustomEmissionFactors(carrier);
    };

    const clearCustomFields = (carrier: GHGCarrierModel) => {
      carrier.customGHGType = undefined;
      carrier.customGHGUnit = undefined;
      carrier.customCO2e_EF = undefined;
      carrier.CustomGHGType = undefined;
      carrier.CustomGHGUnit = undefined;
      carrier.CustomCO2e_EF = undefined;
      carrier.CustomGHGEmissionFactor = undefined;
      carrier.customGHGEmissionFactor = undefined;
    };

    const clearGhgModeOnlyFields = (carrier: GHGCarrierModel) => {
      carrier.ghgType = undefined;
      carrier.ghgUnit = undefined;
      clearCustomFields(carrier);
    };

    payload.ghgCarriers.forEach((carrier) => {
      normalizeLocation(carrier);

      carrier.annualReduction =
        carrier.annualReduction === "" ? 0 : Number(carrier.annualReduction);

      if (!isGHGEmissionMode) {
        // Prevent stale GHG-mode-only values from being sent when saving in Energy mode.
        clearGhgModeOnlyFields(carrier);
      }

      mapGhgToEnergy(carrier);

      const typeIsCustom = isCustomGHGType(carrier);
      const unitIsCustom = isCustomGHGUnit(carrier);

      if (typeIsCustom || unitIsCustom) {
        applyCustomFields(carrier, typeIsCustom, unitIsCustom);
      } else {
        clearCustomFields(carrier);
      }
    });

    return {
      ...payload,
      techLife:
        payload.techLife === ""
          ? 0
          : Number(payload.techLife),
      financialLifetime:
        payload.financialLifetime === ""
          ? 0
          : Number(payload.financialLifetime),
      personnelTime:
        payload.personnelTime === ""
          ? 0
          : Number(payload.personnelTime),
      personnelRate:
        payload.personnelRate === ""
          ? 0
          : Number(payload.personnelRate),
      revenue:
        payload.revenue === ""
          ? 0
          : Number(payload.revenue),
      opEx:
        payload.opEx === ""
          ? 0
          : Number(payload.opEx),
      capExInvestment:
        payload.capExInvestment === ""
          ? 0
          : Number(payload.capExInvestment),
    };
  }

  async function getReductionMeasureStaticList() {
    masterPropertyApi
      .getMasterPropertyListByCategoryAndType(
        MasterPropertyCategories.Location,
        MasterPropertyType.Location.GHGScope
      )
      .then((res) => {
        setGHGScopeList(res);
        setLoading(false);
      });

    setLoading(true);
    masterPropertyApi
      .getMasterPropertyListByCategory(
        MasterPropertyCategories.ReductionMeasure
      )
      .then((res: MasterPropertyModel[]) => {
        if (res.length > 0) {
          setMeasureStageList(
            res.filter(
              (prop) =>
                prop.type === MasterPropertyType.ReductionMeasure.MeasureStage
            )
          );
          setMeasureCategoryList(
            res.filter(
              (prop) =>
                prop.type ===
                MasterPropertyType.ReductionMeasure.MeasureCategory
            )
          );
          setImplementationComplexityList(
            res.filter(
              (prop) =>
                prop.type ===
                MasterPropertyType.ReductionMeasure.MCAImplementationComplexity
            )
          );
          setReputationList(
            res.filter(
              (prop) =>
                prop.type === MasterPropertyType.ReductionMeasure.MCAReputation
            )
          );
          setClimateRiskList(
            res.filter(
              (prop) =>
                prop.type === MasterPropertyType.ReductionMeasure.MCAClimateRisk
            )
          );
          setBiodiversityList(
            res.filter(
              (prop) =>
                prop.type ===
                MasterPropertyType.ReductionMeasure.MCABiodiversity
            )
          );
          setCommunityList(
            res.filter(
              (prop) =>
                prop.type === MasterPropertyType.ReductionMeasure.MCACommunity
            )
          );
          setTechReadinessList(
            res.filter(
              (prop) =>
                prop.type ===
                MasterPropertyType.ReductionMeasure.MCATechRediness
            )
          );
          setOpDisruptionList(
            res.filter(
              (prop) =>
                prop.type ===
                MasterPropertyType.ReductionMeasure.MCAOperationalDisruption
            )
          );
          setHealthSafetyList(
            res.filter(
              (prop) =>
                prop.type ===
                MasterPropertyType.ReductionMeasure.MCAHealthSafety
            )
          );
          setEnergyCategoryList(
            res.filter(
              (prop) =>
                prop.type === MasterPropertyType.ReductionMeasure.EnergyCategory
            )
          );

          setQuarterList(
            res.filter(
              (prop) =>
                prop.type === MasterPropertyType.ReductionMeasure.Quarter
            )
          );
          setLoading(false);
        }
      })
      .catch(() => {
        setLoading(false);
      });

    await fetchLocationDetails();
  }

  const fetchLocationDetails = async () => {
    try {
      const res = await _getLocation(Number(LocationId));
      if (res) {
        const filteredBusinessUnits = res?.businessUnits
          ?.filter((prop) => prop.name) // Remove undefined names
          ?.map((prop) => prop.name);
        setBusinessUnitList(filteredBusinessUnits ?? []);
      }
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  function getMeasureTypeByCategory() {
    if (
      draftReductionMeasure.measureCategory &&
      draftReductionMeasure.measureCategory.length > 0
    ) {
      masterPropertyApi
        .getMasterPropertyListByCategoryAndType(
          MasterPropertyType.ReductionMeasure.MeasureType,
          draftReductionMeasure.measureCategory
        )
        .then((res) => {
          setMeasureTypeList(res);
          setLoading(false);
        });
    }
  }

  function getGHGCategoryList() {
    masterPropertyApi
      .getMasterPropertyListByCategory(MasterPropertyCategories.GHGCategory)
      .then((res) => {
        setGHGCategoryList(res);
        setLoading(false);
      });
  }

  function getGHGCategoryListByScope(ghgScope: string) {
    masterPropertyApi
      .getMasterPropertyListByCategoryAndType(
        MasterPropertyCategories.GHGCategory,
        ghgScope.replaceAll(" ", "")
      )
      .then((res) => {
        const newCatList = ghgCategoryList.concat(res);
        setGHGCategoryList(newCatList);
        setLoading(false);
      });
  }

  function getEnergyTypeList() {
    masterPropertyApi
      .getMasterPropertyListByCategory(MasterPropertyCategories.EnergyType)
      .then((res) => {
        setEnergyTypeList(res);
        setLoading(false);
      });
  }

  function getCountries() {
    masterPropertyApi
      ._getCountries()
      .then((res: EFMappingElectricityModel[]) => {
        if (res) {
          setCountryList(res);
        }
      });
  }

  function getGHGEmissionFactors() {
    const globalFactorsPromise = _getGHGEmissionFactors();
    const clientFactorsPromise =
      clientId && clientId > 0
        ? _getGHGEmissionFactors(clientId)
        : Promise.resolve([] as GHGEmissionFactorModel[]);

    Promise.all([globalFactorsPromise, clientFactorsPromise])
      .then(([globalFactors, clientFactors]) => {
        const mergedByTypeAndUnit = new Map<string, GHGEmissionFactorModel>();

        [...globalFactors, ...clientFactors].forEach((factor) => {
          if (!factor?.ghgType || !factor?.ghgUnit) {
            return;
          }

          const key = `${factor.ghgType}::${factor.ghgUnit}`;
          mergedByTypeAndUnit.set(key, factor);
        });

        setGhgEmissionFactorList(Array.from(mergedByTypeAndUnit.values()));
      })
      .catch((err) => {
        console.error("Error fetching GHG emission factors:", err);
      });
  }

  useEffect(() => {
    setLoading(true);
    getReductionMeasureStaticList();
    if (props.isEdit === true) {
      setLoading(true);
      getReductionMeasureById();
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    getMeasureTypeByCategory();
  }, [draftReductionMeasure.measureCategory]);

  useEffect(() => {
    if (
      draftReductionMeasure.ghgReduction ===
      ReductionMeasureLists.EmissionTypeList[0]
    ) {
      getEnergyTypeList();
      getCountries();
    } else if (isGhgEmissionsSelection(draftReductionMeasure.ghgReduction)) {
      getGHGEmissionFactors();
    }
  }, [draftReductionMeasure.ghgReduction, clientId]);

  // Cleanup: Cancel all in-flight GHG unit requests on component unmount or dialog close
  useEffect(() => {
    return () => {
      Object.values(ghgUnitRequestsRef.current).forEach((controller) => {
        if (controller && !controller.signal.aborted) {
          controller.abort();
        }
      });
      ghgUnitRequestsRef.current = {};
    };
  }, []);

  return (
    <React.Fragment>
      {formErrorMessage && (
        <Alert severity="error" sx={{ mx: 5, mt: 2 }} data-testid="reduction-measure-error-alert">
          {formErrorMessage}
        </Alert>
      )}
      <CreateUpdateReductionMeasuresDesign
        isEdit={props.isEdit}
        fieldChangeHandler={fieldChangeHandler}
        onSave={onSave}
        closeDialog={props.closeDialog}
        ghgCarrierfieldChangeHandler={ghgCarrierfieldChangeHandler}
        draftReductionMeasure={draftReductionMeasure}
        measureStageList={measureStageList}
        measureCategoryList={measureCategoryList}
        measureTypeList={measureTypeList}
        gHGScopeList={gHGScopeList}
        gHGCategoryList={ghgCategoryList}
        implementationComplexityList={implementationComplexityList}
        reputationList={reputationList}
        climateRiskList={climateRiskList}
        biodiversityList={biodiversityList}
        communityList={communityList}
        techReadinessList={techReadinessList}
        opDisruptionList={opDisruptionList}
        healthSafetyList={healthSafetyList}
        energyCategoryList={energyCategoryList}
        energyTypeList={energyTypeList}
        quarterList={quarterList}
        businessUnitList={businessUnitList}
        countryList={countryList}
        regionList={regionList}
        ghgEmissionFactorList={ghgEmissionFactorList}
        ghgUnitOptionsByCarrier={ghgUnitOptionsByCarrier}
      ></CreateUpdateReductionMeasuresDesign>
      <Spinner size={80} data-testid="spinner" open={loading} />
    </React.Fragment>
  );
}
